/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useSettings } from '@/data/Settings';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsPerson } from 'integration/generated/transactions';
import { ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest } from 'integration/generated/transactions/data-contracts';
import { useNotifications } from '@/data/Content/Notifications';
import { processError } from '@/data/utils/processError';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { getLocalization } from '@/data/Localization';
import { ContentProps } from '@/data/types/ContentProps';
import { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useSWRConfig } from 'swr';
import { formatLogonId } from '@/data/utils/formatLogonId';
import {
	getBankPromotionFromLocalStorage,
	useApplyBankPromotionInAuth,
} from '@/data/Content/BanksPromotion';
import { EventsContext } from '@/data/context/events';
import { useContext } from 'react';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';
import { useLoginOrigin } from '@/data/context/loginOrigin';

const registrationFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest,
		params: RequestParams
	) =>
		await transactionsPerson(pub).personRegisterPersonOnUserRegistrationAdminAdd(
			storeId,
			query,
			data,
			params
		);

export type UserRegistration = {
	logonId?: string; // optional for backward compatibility
	// /**
	//  * @deprecated do not use.
	//  */
	email?: string;
	phone1: string;
	firstName: string;
	lastName: string;
	logonPassword: string;
	logonPasswordVerify: string;
	receiveEmail: boolean;
	rememberMe: boolean;
	secondLastName?: string;
	phone?: string;
	lastNameMat: string;
	noticeOfPrivacy: boolean;
	x_recaptchaToken?: string;
};

export const initialRegistration: UserRegistration = {
	logonId: '',
	email: '',
	firstName: '',
	lastName: '',
	logonPassword: '',
	logonPasswordVerify: '',
	phone1: '',
	receiveEmail: true,
	rememberMe: false,
	secondLastName: '',
	lastNameMat: '',
	phone: '',
	noticeOfPrivacy: false,
};

export const getRegistration = async ({ cache, context }: ContentProps) => {
	await getLocalization(cache, context.locale || 'en-US', 'RegistrationLayout');
};

export type RegistrationOrigin =
	| 'account'
	| 'checkout'
	| 'my_orders'
	| 'order_history'
	| 'order_details'
	| 'wishlists'
	// From dialog
	| 'go_to_favorites'
	| 'go_to_account'
	| 'go_to_personalization'
	| 'cart'
	| 'quick_cart'
	| 'saved_to_later'
	| 'saved_to_favorites'
	| 'dp_fan'
	// General
	| 'unknown';

interface RegistrationSubmitProps {
	origin?: RegistrationOrigin;
	shouldCallToModal: boolean;
}

export const useRegistration = (args: RegistrationSubmitProps) => {
	const { settings } = useSettings();
	const { mutate } = useSWRConfig();
	const router = useNextRouter();
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const { langId: preferredLanguage } = getClientSideCommon(settings, router);
	const { onApplyBankPromotionInAuth } = useApplyBankPromotionInAuth();
	const { onSignUp, onLoginLessSubmit } = useContext(EventsContext);
	const login_origin = useLoginOrigin();
	const { onReplaceAuthRoute } = useReplaceAuthRoute({
		shouldCallToModal: !!args.shouldCallToModal,
		origin: args?.origin,
	});

	const registrationSubmit = async (
		props: UserRegistration,
		callbacks?: {
			onClose?: () => Promise<void> | void;
			onAuthSuccess?: () => Promise<void> | void;
		}
	) => {
		const {
			rememberMe,
			receiveEmail,
			email: _email,
			logonId: _logonId,
			logonPassword,
			...rest
		} = props;

		const email = formatLogonId(_email || '');
		const logonId = formatLogonId(_logonId || email);

		const data = <ComIbmCommerceRestMemberHandlerPersonHandlerUserRegistrationAdminAddRequest>{
			...rest,
			logonId,
			email1: email,
			receiveEmail: receiveEmail.toString(),
			logonPassword,
			logonPasswordVerify: logonPassword,
			preferredLanguage,
			profileType: 'C',
			challengeQuestion: '-',
			challengeAnswer: '-',
			receiveEmailPreference: [
				{ value: receiveEmail.toString(), storeId: settings?.storeId ?? '' },
			],
			secondLastName: rest.lastNameMat ?? '',
			lastNameMat: rest.lastNameMat ?? '',
			phone: rest.phone1 ?? '',
			attributes: {
				contactInfoAttrKey: 'addressField1',
				contactInfoAttrValue: rest.lastNameMat,
			},
			x_origin: args.origin ? `web|${args.origin}` : undefined,
			x_bankPromotionId: getBankPromotionFromLocalStorage(),
		};

		try {
			await registrationFetcher(true)(
				settings?.storeId ?? '',
				{ rememberMe, updateCookies: 'true' },
				data,
				params
			);

			const bankPromotion = await onApplyBankPromotionInAuth();

			if (bankPromotion.redirect) {
				await router.push(bankPromotion.redirect);
			} else if (callbacks?.onAuthSuccess) {
				await callbacks.onAuthSuccess();
			} else {
				await callbacks?.onClose?.();
				await onReplaceAuthRoute();
			}

			if (!bankPromotion.success && bankPromotion.message) {
				notifyError(bankPromotion.message);
			}

			onSignUp({
				gtm: {
					method: 'email',
					form_name: 'registro',
					settings,
					origin: args.origin ?? 'unknown',
					login_origin,
				},
			});

			onLoginLessSubmit({
				gtm: {
					settings,
					login_step: 3,
					auth_method: 'password',
					login_status: 'success',
					is_new_user: true,
					login_origin,
				},
			});

			mutate(personMutatorKeyMatcher(''), undefined);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		}
	};

	return {
		registrationSubmit,
		initialRegistration,
	};
};
